import { useState, useEffect, useCallback } from 'react';
import KPICard from '../components/KPICard';
import { useAuthFetch } from '../hooks/useAuthFetch';

// ─── Design tokens (same as rest of app) ─────────────────────────────────────
const C = {
  bg: '#12141f', card: '#1a1d2e', border: '#2a2d3e',
  text: '#e2e8f0', muted: '#94a3b8', faint: '#64748b',
  accent: '#41B073', green: '#22c55e', red: '#ef4444',
  orange: '#f97316', yellow: '#eab308',
};
const inputStyle = {
  padding: '8px 12px', borderRadius: 8, background: C.card, border: `1px solid ${C.border}`,
  color: C.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box',
};
const selectStyle = { ...inputStyle, cursor: 'pointer' };
const labelStyle = { fontSize: 12, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 5 };

const Badge = ({ text, color }) => (
  <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 6, padding: '2px 9px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{text}</span>
);

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

// ─── Score & Recommendations ──────────────────────────────────────────────────
const SCORE_FIELDS = ['qualite_donnees', 'accessibilite_donnees', 'structuration_donnees', 'maturite_data_global', 'maturite_digitale'];

function computeScore(audit) {
  const filled = SCORE_FIELDS.filter(f => Number(audit[f]) > 0);
  if (filled.length === 0) return null;
  const sum = SCORE_FIELDS.reduce((acc, f) => acc + (Number(audit[f]) || 0), 0);
  return Math.round(sum / 25 * 100);
}

const LEVELS = [
  { min: 0,  max: 20,  n: 1, label: 'Niveau 1', desc: 'Maturité très faible', color: C.red },
  { min: 21, max: 40,  n: 2, label: 'Niveau 2', desc: 'Maturité faible',       color: C.orange },
  { min: 41, max: 60,  n: 3, label: 'Niveau 3', desc: 'Intermédiaire',          color: C.yellow },
  { min: 61, max: 80,  n: 4, label: 'Niveau 4', desc: 'Bonne maturité',         color: C.green },
  { min: 81, max: 100, n: 5, label: 'Niveau 5', desc: 'Maturité élevée',        color: C.accent },
];
const getLevel = pct => LEVELS.find(l => pct >= l.min && pct <= l.max) || LEVELS[0];

const RECO = {
  1: {
    diagnostic: "Les données sont éparses, peu accessibles et non structurées. Avant tout outil de visualisation, un travail de structuration et de centralisation est indispensable.",
    service: "Audit data seul",
    fourchette: "500 – 1 500 €",
    delai: "2 – 4 semaines",
    maintenance: null,
    actions: ["Cartographier et qualifier toutes les sources de données", "Définir un plan de structuration et de centralisation", "Former les équipes aux bonnes pratiques de saisie"],
  },
  2: {
    diagnostic: "Quelques données sont disponibles mais difficiles d'accès. Un dashboard simple sur les données existantes apporterait une première valeur concrète et rapide.",
    service: "Dashboard Essentiel",
    fourchette: "1 500 – 3 000 €",
    delai: "3 – 6 semaines",
    maintenance: "Maintenance Essentiel · 150 – 250 €/mois",
    actions: ["Identifier les 3 à 5 KPIs prioritaires", "Connecter les sources accessibles (exports, fichiers)", "Livrer un dashboard simple et intuitif"],
  },
  3: {
    diagnostic: "La base data est présente mais le reporting reste manuel. Un dashboard intermédiaire permettrait d'automatiser les synthèses et de gagner en réactivité.",
    service: "Dashboard Intermédiaire",
    fourchette: "3 000 – 6 000 €",
    delai: "4 – 8 semaines",
    maintenance: "Maintenance Essentiel ou Évolutions · 250 – 500 €/mois",
    actions: ["Connecter toutes les sources pertinentes", "Automatiser les rapports manuels actuels", "Mettre en place des alertes sur seuils"],
  },
  4: {
    diagnostic: "Bonne maturité data. Le client est prêt pour un outil avancé avec interactions, connexions multi-sources et vues personnalisées par profil.",
    service: "Dashboard Avancé ou Application métier",
    fourchette: "6 000 – 12 000 €",
    delai: "6 – 12 semaines",
    maintenance: "Maintenance Évolutions · 300 – 600 €/mois",
    actions: ["Définir les flux de données et les automatisations", "Développer des vues interactives par profil utilisateur", "Intégrer les APIs métier existantes"],
  },
  5: {
    diagnostic: "Maturité data élevée. Le client est prêt pour une application métier sur mesure, avec intégrations avancées et architecture data dédiée.",
    service: "Application métier sur mesure",
    fourchette: "> 12 000 €",
    delai: "3 – 6 mois",
    maintenance: "Maintenance Évolutions + Régie · > 600 €/mois",
    actions: ["Concevoir une architecture data personnalisée", "Développer une application interactive complète", "Mettre en place des intégrations API avancées"],
  },
};

const SECTOR_KPIS = {
  'Commerce': ['CA journalier / hebdo', 'Panier moyen', 'Marge brute par produit', 'Taux de rotation des stocks', 'Top 10 produits vendus', 'Taux de conversion'],
  'Industrie': ['Taux de production', 'Taux de rebuts / non-conformités', 'OEE (rendement global)', 'Délai de livraison moyen', 'Coût de revient unitaire', 'Taux de pannes'],
  'BTP': ['Avancement chantier (%)', 'Coût réalisé vs budget', 'Heures main-d\'œuvre', 'Délai de facturation', 'Marge par chantier', 'Incidents sécurité'],
  'Agriculture': ['Rendement par parcelle (q/ha)', 'Coût intrant par hectare', 'Marge nette par culture', 'Consommation eau / énergie', 'Évolution météo vs rendement'],
  'Logistique': ['Taux de livraison à l\'heure', 'Coût carburant / km', 'Taux de remplissage véhicules', 'Nombre d\'incidents / retards', 'Kilométrage à vide'],
  'Services BtoB': ['CA par client', 'Taux de renouvellement contrats', 'Délai de règlement moyen', 'Heures facturées vs produites', 'NPS client'],
  'Agri-Agro': ['Suivi des lots / traçabilité', 'Conformité fournisseurs', 'Coûts de transformation', 'Pertes / déchets', 'Délais de rotation stocks'],
};

const STATUT_COLORS = { Chaud: C.red, Tiède: C.orange, Froid: C.faint, Confirmé: C.green, Abandonné: '#475569' };

// ─── Form UI components (dark) ────────────────────────────────────────────────

function Rating5({ label, value, onChange, hint }) {
  const [lo, hi] = hint ? hint.split('→').map(s => s.replace(/^\d\s*=\s*/, '').trim()) : ['', ''];
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      {hint && <div style={{ fontSize: 11, color: C.faint, marginBottom: 6 }}>1 = {lo} · 5 = {hi}</div>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => onChange(n)} style={{
            width: 34, height: 34, borderRadius: 8,
            border: `2px solid ${value === n ? C.accent : C.border}`,
            background: value === n ? C.accent : C.card,
            color: value === n ? '#fff' : C.muted,
            fontWeight: 700, cursor: 'pointer', fontSize: 13,
          }}>{n}</button>
        ))}
        {value > 0 && (
          <button type="button" onClick={() => onChange(0)}
            style={{ fontSize: 11, color: C.faint, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        )}
      </div>
    </div>
  );
}

function CheckGroup({ label, options, value = [], onChange }) {
  const toggle = opt => onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={labelStyle}>{label}</label>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {options.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: C.muted }}>
            <input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)} style={{ accentColor: C.accent }} />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

function RadioGrp({ label, options, value, onChange }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={labelStyle}>{label}</label>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {options.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: C.muted }}>
            <input type="radio" checked={value === opt} onChange={() => onChange(opt)} style={{ accentColor: C.accent }} />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );
}

function TA({ label, value, onChange, placeholder, hint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={labelStyle}>{label}</label>}
      {hint && <div style={{ fontSize: 11, color: C.faint, marginBottom: 5, fontStyle: 'italic' }}>{hint}</div>}
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
    </div>
  );
}

function FInput({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={labelStyle}>{label}</label>}
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
    </div>
  );
}

function FSelect({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={labelStyle}>{label}</label>}
      <select value={value || ''} onChange={e => onChange(e.target.value)} style={selectStyle}>
        <option value="">— Sélectionner —</option>
        {options.map(o => typeof o === 'string' ? <option key={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SBlock({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>{title}</div>
      {children}
    </div>
  );
}

function InfoBox({ children, color = C.accent }) {
  return (
    <div style={{ background: color + '15', border: `1px solid ${color}30`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: C.muted }}>
      {children}
    </div>
  );
}

// ─── Dynamic tables ───────────────────────────────────────────────────────────

const emptySource = () => ({ source: '', type: '', format: '', frequence: '', accessible: '', volume: '', commentaire: '' });
const emptyKpi    = () => ({ indicateur: '', metier: '', donnee_source: '', priorite: '', commentaire: '' });
const emptyProfil = () => ({ profil: '', role: '', niveau: '', usage: '', mobile: '', commentaire: '' });

const tinput = (val, cb, w) => (
  <input value={val || ''} onChange={e => cb(e.target.value)}
    style={{ width: w || 110, padding: '5px 7px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 12 }} />
);
const tselect = (val, cb, opts, w) => (
  <select value={val || ''} onChange={e => cb(e.target.value)}
    style={{ width: w || 90, padding: '5px 7px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontSize: 12 }}>
    <option value="">—</option>
    {opts.map(o => <option key={o}>{o}</option>)}
  </select>
);
const TH = ({ children, w }) => <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, color: C.faint, fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap', width: w }}>{children}</th>;
const TD = ({ children }) => <td style={{ padding: '4px 5px' }}>{children}</td>;
const DelBtn = ({ onClick }) => <button type="button" onClick={onClick} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>;

function SourcesTable({ rows, onChange }) {
  const upd = (i, k, v) => onChange(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  return (
    <div style={{ overflowX: 'auto', marginBottom: 12 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: C.bg }}>
          <TH>Source</TH><TH>Type</TH><TH>Format</TH><TH>Fréq. MAJ</TH><TH>Accessible</TH><TH>Volume</TH><TH w={160}>Commentaire</TH><TH></TH>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
              <TD>{tinput(r.source,      v => upd(i,'source',v),      130)}</TD>
              <TD>{tinput(r.type,        v => upd(i,'type',v),         90)}</TD>
              <TD>{tinput(r.format,      v => upd(i,'format',v),       80)}</TD>
              <TD>{tinput(r.frequence,   v => upd(i,'frequence',v),    90)}</TD>
              <TD>{tselect(r.accessible, v => upd(i,'accessible',v), ['Oui','Non'], 70)}</TD>
              <TD>{tinput(r.volume,      v => upd(i,'volume',v),       80)}</TD>
              <TD>{tinput(r.commentaire, v => upd(i,'commentaire',v), 155)}</TD>
              <TD><DelBtn onClick={() => onChange(rows.filter((_,j) => j!==i))} /></TD>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={() => onChange([...rows, emptySource()])}
        style={{ marginTop: 8, padding: '5px 12px', borderRadius: 6, border: `1.5px dashed ${C.accent}66`, background: 'none', color: C.accent, fontSize: 12, cursor: 'pointer' }}>
        + Ajouter une source
      </button>
    </div>
  );
}

function KpisTable({ rows, onChange, secteur }) {
  const upd = (i, k, v) => onChange(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const suggestions = SECTOR_KPIS[secteur] || [];
  const addSuggestion = kpi => {
    if (!rows.find(r => r.indicateur === kpi)) onChange([...rows, { ...emptyKpi(), indicateur: kpi }]);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      {suggestions.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: C.faint, marginBottom: 6 }}>Suggestions pour le secteur « {secteur} » — cliquez pour ajouter :</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {suggestions.map(s => (
              <button key={s} type="button" onClick={() => addSuggestion(s)}
                style={{ padding: '3px 10px', borderRadius: 12, border: `1px solid ${C.accent}44`, background: C.accent + '15', color: C.accent, fontSize: 11, cursor: 'pointer' }}>
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: C.bg }}>
            <TH w={200}>Indicateur / KPI</TH><TH>Métier</TH><TH>Donnée source</TH><TH>Priorité</TH><TH w={160}>Commentaire</TH><TH></TH>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                <TD>{tinput(r.indicateur,    v => upd(i,'indicateur',v),    195)}</TD>
                <TD>{tinput(r.metier,        v => upd(i,'metier',v),        110)}</TD>
                <TD>{tinput(r.donnee_source, v => upd(i,'donnee_source',v), 110)}</TD>
                <TD>{tselect(r.priorite,     v => upd(i,'priorite',v), ['1 — Indispensable','2 — Important','3 — Nice to have'], 150)}</TD>
                <TD>{tinput(r.commentaire,   v => upd(i,'commentaire',v),   155)}</TD>
                <TD><DelBtn onClick={() => onChange(rows.filter((_,j) => j!==i))} /></TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={() => onChange([...rows, emptyKpi()])}
        style={{ marginTop: 8, padding: '5px 12px', borderRadius: 6, border: `1.5px dashed ${C.accent}66`, background: 'none', color: C.accent, fontSize: 12, cursor: 'pointer' }}>
        + Ajouter un KPI
      </button>
    </div>
  );
}

function ProfilsTable({ rows, onChange }) {
  const upd = (i, k, v) => onChange(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  return (
    <div style={{ overflowX: 'auto', marginBottom: 12 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ background: C.bg }}>
          <TH>Profil</TH><TH>Rôle / Poste</TH><TH>Niveau numérique</TH><TH>Usage prévu</TH><TH>Mobile</TH><TH w={130}>Commentaire</TH><TH></TH>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
              <TD>{tinput(r.profil,      v => upd(i,'profil',v),      120)}</TD>
              <TD>{tinput(r.role,        v => upd(i,'role',v),        120)}</TD>
              <TD>{tselect(r.niveau,     v => upd(i,'niveau',v), ['Débutant','Moyen','Expert'], 110)}</TD>
              <TD>{tinput(r.usage,       v => upd(i,'usage',v),       130)}</TD>
              <TD>{tselect(r.mobile,     v => upd(i,'mobile',v), ['Oui','Non'], 65)}</TD>
              <TD>{tinput(r.commentaire, v => upd(i,'commentaire',v), 125)}</TD>
              <TD><DelBtn onClick={() => onChange(rows.filter((_,j) => j!==i))} /></TD>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={() => onChange([...rows, emptyProfil()])}
        style={{ marginTop: 8, padding: '5px 12px', borderRadius: 6, border: `1.5px dashed ${C.accent}66`, background: 'none', color: C.accent, fontSize: 12, cursor: 'pointer' }}>
        + Ajouter un profil
      </button>
    </div>
  );
}

// ─── Wizard steps ─────────────────────────────────────────────────────────────

function Step1({ form, set, clientOptions }) {
  return (
    <>
      <SBlock title="Fiche de mission">
        <div className="form-grid" style={{ gap: '0 20px' }}>
          <div>
            <label style={labelStyle}>Entreprise cliente</label>
            <select value={form.client_id || ''} onChange={e => set('client_id', e.target.value)} style={selectStyle}>
              {clientOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          {!form.client_id && <FInput label="Nom (saisie libre)" value={form.client_nom_saisi} onChange={v => set('client_nom_saisi', v)} placeholder="Nom de l'entreprise" />}
          <FInput label="Interlocuteur principal" value={form.interlocuteur} onChange={v => set('interlocuteur', v)} />
          <FInput label="Poste / Fonction" value={form.poste_fonction} onChange={v => set('poste_fonction', v)} />
          <FInput label="Date de l'audit" value={form.date_audit} onChange={v => set('date_audit', v)} type="date" />
        </div>
      </SBlock>
      <SBlock title="1.1 Informations générales">
        <div className="form-grid" style={{ gap: '0 20px' }}>
          <FInput label="Secteur d'activité" value={form.secteur} onChange={v => set('secteur', v)} placeholder="Commerce, Industrie, BTP…" />
          <FInput label="Nombre de salariés" value={form.nb_salaries} onChange={v => set('nb_salaries', v)} />
          <FSelect label="CA annuel approximatif" value={form.ca_annuel} onChange={v => set('ca_annuel', v)} options={['< 500K€','500K–2M€','2M–10M€','> 10M€']} />
          <FInput label="Localisation" value={form.localisation} onChange={v => set('localisation', v)} placeholder="Ville / Département" />
          <FInput label="Site web" value={form.site_web} onChange={v => set('site_web', v)} placeholder="www.exemple.fr" />
        </div>
      </SBlock>
      <SBlock title="1.2 Activité & contexte">
        <TA label="Décrivez votre activité principale" value={form.activite_description} onChange={v => set('activite_description', v)}
          hint="Que faites-vous ? Pour quels clients ? Avec quels process clés ?" placeholder="Réponse libre…" />
        <TA label="Quels sont vos principaux défis actuels ?" value={form.defis_actuels} onChange={v => set('defis_actuels', v)}
          hint="Croissance, concurrence, digitalisation, manque de visibilité sur les chiffres…" placeholder="Réponse libre…" />
        <RadioGrp label="Votre entreprise est-elle en phase de :"
          options={['Stabilisation','Croissance','Transformation','Diversification','Restructuration']}
          value={form.phase} onChange={v => set('phase', v)} />
      </SBlock>
    </>
  );
}

function Step2({ form, set }) {
  return (
    <>
      <SBlock title="2.1 Sources disponibles">
        <CheckGroup label="Fichiers & bureautique" options={['Excel / Sheets','CSV / exports','Word / PDF','Emails']} value={form.sources_fichiers} onChange={v => set('sources_fichiers', v)} />
        <CheckGroup label="Logiciels métier" options={['ERP (Sage, SAP…)','CRM (Salesforce, HubSpot…)','Logiciel comptable','Logiciel de caisse','GMAO','WMS / logistique']} value={form.sources_logiciels} onChange={v => set('sources_logiciels', v)} />
        <CheckGroup label="Sources numériques" options={['Site e-commerce','Google Analytics / SEO','Réseaux sociaux','API externe','Base de données SQL','Autre']} value={form.sources_numeriques} onChange={v => set('sources_numeriques', v)} />
        <CheckGroup label="Sources physiques / manuelles" options={['Fichiers papier','Pointage manuel','Relevés terrain','Données fournisseurs']} value={form.sources_physiques} onChange={v => set('sources_physiques', v)} />
      </SBlock>
      <SBlock title="2.2 Cartographie détaillée des sources">
        <SourcesTable rows={form.sources_table || []} onChange={v => set('sources_table', v)} />
      </SBlock>
      <SBlock title="2.3 Qualité & accessibilité des données">
        <InfoBox>Ces 4 critères alimentent directement le score de maturité data du client.</InfoBox>
        <div style={{ background: C.bg, borderRadius: 10, padding: 16 }}>
          <Rating5 label="Qualité générale des données" value={form.qualite_donnees} onChange={v => set('qualite_donnees', v)} hint="1 = Très mauvaise → 5 = Excellente" />
          <Rating5 label="Facilité d'accès aux données" value={form.accessibilite_donnees} onChange={v => set('accessibilite_donnees', v)} hint="1 = Très difficile → 5 = Très facile" />
          <Rating5 label="Niveau de structuration" value={form.structuration_donnees} onChange={v => set('structuration_donnees', v)} hint="1 = Non structurées → 5 = Parfaitement structurées" />
          <Rating5 label="Score maturité data global" value={form.maturite_data_global} onChange={v => set('maturite_data_global', v)} hint="1 = Très faible → 5 = Très élevée" />
        </div>
        <TA label="Problèmes principaux avec les données actuelles" value={form.problemes_donnees} onChange={v => set('problemes_donnees', v)}
          hint="Doublons, formats incompatibles, mises à jour manuelles, erreurs fréquentes…" placeholder="Réponse libre…" />
        <CheckGroup label="Des données sont-elles sensibles / confidentielles ?"
          options={['Oui — données RH','Oui — données financières','Oui — données clients','Non, pas de contrainte particulière']}
          value={form.donnees_sensibles} onChange={v => set('donnees_sensibles', v)} />
      </SBlock>
    </>
  );
}

function Step3({ form, set }) {
  return (
    <>
      <SBlock title="3.1 Environnement informatique">
        <div className="form-grid" style={{ gap: '0 20px' }}>
          <RadioGrp label="Système d'exploitation" options={['Windows','macOS','Linux','Mixte']} value={form.os} onChange={v => set('os', v)} />
          <RadioGrp label="Hébergement des données" options={['Local','Cloud','NAS','Mixte','NSP']} value={form.hebergement} onChange={v => set('hebergement', v)} />
        </div>
        <FInput label="Logiciels principaux" value={form.logiciels_principaux} onChange={v => set('logiciels_principaux', v)} placeholder="ERP, CRM, outils de gestion…" />
        <FInput label="Outils de reporting actuels" value={form.outils_reporting} onChange={v => set('outils_reporting', v)} placeholder="Excel, Power BI, Tableau, aucun…" />
        <FInput label="Service IT / Prestataire" value={form.service_it} onChange={v => set('service_it', v)} placeholder="Nom et contact si applicable" />
      </SBlock>
      <SBlock title="3.2 Maturité digitale">
        <InfoBox>Ces critères complètent le score de maturité data.</InfoBox>
        <div style={{ background: C.bg, borderRadius: 10, padding: 16 }}>
          <Rating5 label="Maturité digitale globale" value={form.maturite_digitale} onChange={v => set('maturite_digitale', v)} hint="1 = Très faible → 5 = Très élevée" />
          <Rating5 label="Compétence informatique des équipes" value={form.competence_equipes} onChange={v => set('competence_equipes', v)} hint="1 = Très faible → 5 = Très élevée" />
        </div>
        <RadioGrp label="Outils de visualisation déjà en place ?"
          options={['Oui, on est satisfait','Oui, mais insuffisants','Non, on utilise Excel','Non, rien du tout']}
          value={form.outils_visu} onChange={v => set('outils_visu', v)} />
        <TA label="Ce qui ne fonctionne pas avec les outils actuels" value={form.outils_problemes} onChange={v => set('outils_problemes', v)} placeholder="Réponse libre…" />
      </SBlock>
      <SBlock title="3.3 Contraintes techniques">
        <CheckGroup label="Contraintes à prendre en compte"
          options={['RGPD / données personnelles','Hébergement France obligatoire',"Pas d'accès cloud","Pas d'installation possible",'Aucune contrainte']}
          value={form.contraintes_tech} onChange={v => set('contraintes_tech', v)} />
        <div className="form-grid" style={{ gap: '0 20px' }}>
          <FInput label="Intégrations API nécessaires ?" value={form.api_integrations} onChange={v => set('api_integrations', v)} placeholder="Quels systèmes doivent communiquer ?" />
          <RadioGrp label="Accès cloud autorisé ?" options={['Oui','Non','Partiel']} value={form.cloud_autorise} onChange={v => set('cloud_autorise', v)} />
        </div>
        <FInput label="Autres contraintes" value={form.autres_contraintes_tech} onChange={v => set('autres_contraintes_tech', v)} />
      </SBlock>
    </>
  );
}

function Step4({ form, set }) {
  return (
    <>
      <SBlock title="4.1 Problème principal à résoudre">
        <TA label="Quel est le problème numéro 1 que vous souhaitez résoudre ?"
          value={form.probleme_principal} onChange={v => set('probleme_principal', v)}
          hint="Ex : Je passe 3h par semaine à compiler des rapports manuellement. Je n'ai pas de vision en temps réel de mes ventes…"
          placeholder="Réponse libre…" />
        <TA label="Qu'est-ce qui vous a poussé à chercher une solution maintenant ?"
          value={form.declencheur} onChange={v => set('declencheur', v)} placeholder="Réponse libre…" />
      </SBlock>
      <SBlock title="4.2 KPI & indicateurs souhaités">
        <InfoBox color={C.green}>Priorité : 1 = Indispensable · 2 = Important · 3 = Nice to have{form.secteur ? ` — Suggestions disponibles pour le secteur « ${form.secteur} »` : ''}</InfoBox>
        <KpisTable rows={form.kpis_table || []} onChange={v => set('kpis_table', v)} secteur={form.secteur} />
      </SBlock>
      <SBlock title="4.3 Type de livrable souhaité">
        <CheckGroup label="Type d'outil"
          options={['Dashboard de suivi (lecture)','Application interactive (saisie + lecture)','Rapport automatique (PDF)','Alerte / notification','NSP']}
          value={form.type_livrable} onChange={v => set('type_livrable', v)} />
        <RadioGrp label="Fréquence de consultation"
          options={['Temps réel','Quotidien','Hebdomadaire','Mensuel','À la demande']}
          value={form.frequence_consultation} onChange={v => set('frequence_consultation', v)} />
        <CheckGroup label="Supports cibles"
          options={['Ordinateur bureau','Ordinateur portable','Tablette','Smartphone','TV / écran mural']}
          value={form.supports_cibles} onChange={v => set('supports_cibles', v)} />
      </SBlock>
      <SBlock title="4.4 Résultats attendus">
        <TA label="Dans 6 mois, comment saurez-vous que l'outil est un succès ?" value={form.definition_succes} onChange={v => set('definition_succes', v)} placeholder="Réponse libre…" />
        <div style={{ background: C.bg, borderRadius: 10, padding: 16 }}>
          <Rating5 label="Urgence du projet" value={form.urgence} onChange={v => set('urgence', v)} hint="1 = Pas urgente → 5 = Très urgente" />
          <Rating5 label="Impact attendu" value={form.impact} onChange={v => set('impact', v)} hint="1 = Faible → 5 = Transformateur" />
        </div>
      </SBlock>
    </>
  );
}

function Step5({ form, set }) {
  return (
    <>
      <SBlock title="5.1 Profils utilisateurs">
        <ProfilsTable rows={form.profils_table || []} onChange={v => set('profils_table', v)} />
      </SBlock>
      <SBlock title="5.2 Gouvernance du projet">
        <div className="form-grid" style={{ gap: '0 20px' }}>
          <FInput label="Décideur final" value={form.decideur} onChange={v => set('decideur', v)} />
          <FInput label="Référent projet côté client" value={form.referent_projet} onChange={v => set('referent_projet', v)} />
        </div>
        <CheckGroup label="Personnes impliquées dans le projet"
          options={['Direction','IT','Métier','Utilisateurs finaux','Finance']}
          value={form.personnes_impliquees} onChange={v => set('personnes_impliquees', v)} />
        <TA label="Résistances internes à anticiper" value={form.resistances} onChange={v => set('resistances', v)} placeholder="Réponse libre…" />
      </SBlock>
      <SBlock title="5.3 Disponibilité & organisation">
        <RadioGrp label="Disponibilité pour les ateliers"
          options={['1-2h/semaine','Demi-journée/mois','Selon avancement','Très limitée']}
          value={form.disponibilite} onChange={v => set('disponibilite', v)} />
        <RadioGrp label="Mode de collaboration préféré"
          options={['Présentiel (Amiens / alentours)','Visio','Mixte','Asynchrone (email / messagerie)']}
          value={form.collaboration} onChange={v => set('collaboration', v)} />
      </SBlock>
    </>
  );
}

function Step6({ form, set }) {
  return (
    <>
      <SBlock title="6.1 Enveloppe budgétaire">
        <RadioGrp label="Budget projet initial"
          options={['< 1 000 €','1 000 – 3 000 €','3 000 – 6 000 €','6 000 – 12 000 €','> 12 000 €','À définir']}
          value={form.budget_projet} onChange={v => set('budget_projet', v)} />
        <RadioGrp label="Budget maintenance mensuel"
          options={['< 150 €/mois','150 – 300 €/mois','300 – 600 €/mois','> 600 €/mois','NSP']}
          value={form.budget_maintenance} onChange={v => set('budget_maintenance', v)} />
        <TA label="Contraintes budgétaires particulières" value={form.contraintes_budget} onChange={v => set('contraintes_budget', v)}
          placeholder="Année fiscale, validation direction, subvention…" />
      </SBlock>
      <SBlock title="6.2 Délais & contraintes">
        <div className="form-grid" style={{ gap: '0 20px' }}>
          <FInput label="Date de livraison souhaitée" value={form.date_livraison} onChange={v => set('date_livraison', v)} type="date" />
          <CheckGroup label="Contraintes de délai"
            options={['Événement interne','Saisonnalité','Engagement client','Aucune']}
            value={form.contrainte_delai} onChange={v => set('contrainte_delai', v)} />
        </div>
        <FInput label="Autres contraintes" value={form.autres_contraintes_delai} onChange={v => set('autres_contraintes_delai', v)} />
      </SBlock>
    </>
  );
}

function Step7({ form, set }) {
  const score = computeScore(form);
  const level = score !== null ? getLevel(score) : null;
  const reco = level ? RECO[level.n] : null;
  return (
    <>
      <InfoBox color={C.orange}>⚠️ Section réservée à LA DONNÉE — ne pas partager avec le client</InfoBox>

      {reco && (
        <div style={{ background: C.bg, border: `1px solid ${level.color}44`, borderLeft: `4px solid ${level.color}`, borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: level.color }}>{level.label} — {level.desc} · {score}%</div>
              <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>Score de maturité calculé automatiquement</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{reco.service}</div>
              <div style={{ fontSize: 11, color: C.green }}>{reco.fourchette}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>{reco.diagnostic}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {reco.actions.map(a => (
              <span key={a} style={{ fontSize: 11, padding: '2px 9px', borderRadius: 10, background: level.color + '22', color: level.color }}>✓ {a}</span>
            ))}
          </div>
          {reco.maintenance && <div style={{ marginTop: 8, fontSize: 11, color: C.faint }}>Maintenance suggérée : {reco.maintenance}</div>}
        </div>
      )}

      <SBlock title="7.1 Analyse">
        <TA label="Problème principal (reformulation)" value={form.reformulation_probleme} onChange={v => set('reformulation_probleme', v)} placeholder="Reformuler le besoin client avec nos mots…" />
        <TA label="Données disponibles & qualité estimée" value={form.donnees_qualite_estimee} onChange={v => set('donnees_qualite_estimee', v)} placeholder="Synthèse de ce que le client a réellement…" />
        <Rating5 label="Complexité technique estimée" value={form.complexite_estimee} onChange={v => set('complexite_estimee', v)} hint="1 = Simple → 5 = Très complexe" />
        <TA label="Risques identifiés" value={form.risques} onChange={v => set('risques', v)} placeholder="Dépendances, données manquantes, équipe non impliquée…" />
      </SBlock>
      <SBlock title="7.2 Proposition de solution">
        <RadioGrp label="Type de livrable recommandé"
          options={['Dashboard simple','Dashboard avancé','Application métier','Audit data seul','À préciser']}
          value={form.livrable_recommande} onChange={v => set('livrable_recommande', v)} />
        <TA label="Périmètre fonctionnel suggéré" value={form.perimetre_fonctionnel} onChange={v => set('perimetre_fonctionnel', v)} placeholder="KPI, sources, niveau d'interactivité…" />
        <div className="form-grid" style={{ gap: '0 20px' }}>
          <FInput label="Estimation indicative" value={form.estimation_indicative} onChange={v => set('estimation_indicative', v)} placeholder="Ex : 2 500 – 4 000 € · 5-6 semaines" />
          <Rating5 label="Niveau de complexité" value={form.niveau_complexite} onChange={v => set('niveau_complexite', v)} hint="1 = Simple → 5 = Très complexe" />
        </div>
      </SBlock>
      <SBlock title="7.3 Suivi & prochaines étapes">
        <div className="form-grid" style={{ gap: '0 20px' }}>
          <RadioGrp label="Statut prospect"
            options={['Chaud','Tiède','Froid','Confirmé','Abandonné']}
            value={form.statut_prospect} onChange={v => set('statut_prospect', v)} />
          <FInput label="Date de relance prévue" value={form.date_relance} onChange={v => set('date_relance', v)} type="date" />
        </div>
        <FInput label="Documents à demander au client" value={form.documents_demander} onChange={v => set('documents_demander', v)} />
        <TA label="Actions à mener de notre côté" value={form.actions} onChange={v => set('actions', v)} placeholder="Notes libres…" />
        <TA label="Notes complémentaires" value={form.notes_complementaires} onChange={v => set('notes_complementaires', v)} placeholder="Notes libres…" />
      </SBlock>
    </>
  );
}

// ─── Wizard shell ─────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, icon: '🏢', label: 'Présentation' },
  { id: 2, icon: '🗄️', label: 'Données & Sources' },
  { id: 3, icon: '💻', label: 'Outils & Systèmes' },
  { id: 4, icon: '🎯', label: 'Besoins & Objectifs' },
  { id: 5, icon: '👥', label: 'Organisation' },
  { id: 6, icon: '💶', label: 'Budget & Contraintes' },
  { id: 7, icon: '📋', label: 'Synthèse Auditeur' },
];

const defaultForm = {
  client_id: '', client_nom_saisi: '', interlocuteur: '', poste_fonction: '', date_audit: '',
  secteur: '', nb_salaries: '', ca_annuel: '', localisation: '', site_web: '',
  activite_description: '', defis_actuels: '', phase: '',
  sources_fichiers: [], sources_logiciels: [], sources_numeriques: [], sources_physiques: [], sources_table: [],
  qualite_donnees: 0, accessibilite_donnees: 0, structuration_donnees: 0, maturite_data_global: 0,
  problemes_donnees: '', donnees_sensibles: [],
  os: '', hebergement: '', logiciels_principaux: '', outils_reporting: '', service_it: '',
  maturite_digitale: 0, competence_equipes: 0, outils_visu: '', outils_problemes: '',
  contraintes_tech: [], api_integrations: '', cloud_autorise: '', autres_contraintes_tech: '',
  probleme_principal: '', declencheur: '', kpis_table: [],
  type_livrable: [], frequence_consultation: '', supports_cibles: [],
  definition_succes: '', urgence: 0, impact: 0,
  profils_table: [], decideur: '', referent_projet: '', personnes_impliquees: [], resistances: '',
  disponibilite: '', collaboration: '',
  budget_projet: '', budget_maintenance: '', contraintes_budget: '',
  date_livraison: '', contrainte_delai: [], autres_contraintes_delai: '',
  reformulation_probleme: '', donnees_qualite_estimee: '', complexite_estimee: 0, risques: '',
  livrable_recommande: '', perimetre_fonctionnel: '', estimation_indicative: '',
  niveau_complexite: 0, statut_prospect: '', date_relance: '',
  documents_demander: '', actions: '', notes_complementaires: '',
};

function AuditWizard({ clients, initial, onSave, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => initial ? { ...defaultForm, ...initial } : { ...defaultForm });
  const [saving, setSaving] = useState(false);
  const set = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);

  const score = computeScore(form);
  const level = score !== null ? getLevel(score) : null;

  const handleSave = async () => { setSaving(true); await onSave(form); setSaving(false); };

  const clientOptions = [
    { value: '', label: '— Client libre (saisie manuelle) —' },
    ...clients.map(c => ({ value: String(c.id), label: c.nom })),
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', flex: 1, margin: '20px', borderRadius: 14, overflow: 'hidden', background: C.bg }}>

        {/* Sidebar */}
        <div style={{ width: 210, background: '#0d0f1a', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${C.border}` }}>
          <div style={{ padding: '18px 16px 14px', borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: 1 }}>Audit Client</div>
            <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>LA DONNÉE</div>
          </div>
          <div style={{ flex: 1, paddingTop: 8 }}>
            {STEPS.map(s => (
              <button key={s.id} type="button" onClick={() => setStep(s.id)} style={{
                width: '100%', padding: '9px 16px', textAlign: 'left', background: step === s.id ? C.accent + '25' : 'none',
                border: 'none', borderLeft: `3px solid ${step === s.id ? C.accent : 'transparent'}`,
                color: step === s.id ? C.text : C.faint,
                cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span>{s.icon}</span>
                <span style={{ flex: 1 }}>{s.label}</span>
                {s.id === 7 && <span style={{ fontSize: 9, background: '#ef444433', color: '#fca5a5', padding: '1px 5px', borderRadius: 4 }}>Interne</span>}
              </button>
            ))}
          </div>
          {score !== null && level && (
            <div style={{ padding: '14px 16px', borderTop: `1px solid ${C.border}`, textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: C.faint, marginBottom: 3 }}>Score maturité</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: level.color, fontFamily: "'DM Mono', monospace" }}>{score}%</div>
              <div style={{ fontSize: 10, color: level.color, marginBottom: 6 }}>{level.label}</div>
              <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${score}%`, background: level.color, borderRadius: 3, transition: 'width 0.4s' }} />
              </div>
              {RECO[level.n] && (
                <div style={{ marginTop: 8, fontSize: 10, color: C.green, fontWeight: 600 }}>{RECO[level.n].service}</div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '14px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{STEPS[step-1].icon} {STEPS[step-1].label}</div>
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: C.faint, cursor: 'pointer' }}>✕</button>
          </div>
          <div className="modal-scroll" style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            {step === 1 && <Step1 form={form} set={set} clientOptions={clientOptions} />}
            {step === 2 && <Step2 form={form} set={set} />}
            {step === 3 && <Step3 form={form} set={set} />}
            {step === 4 && <Step4 form={form} set={set} />}
            {step === 5 && <Step5 form={form} set={set} />}
            {step === 6 && <Step6 form={form} set={set} />}
            {step === 7 && <Step7 form={form} set={set} />}
          </div>
          <div style={{ padding: '12px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button type="button" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
              style={{ padding: '7px 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', color: step === 1 ? C.border : C.muted, cursor: step === 1 ? 'not-allowed' : 'pointer', fontSize: 13 }}>
              ← Précédent
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={handleSave} disabled={saving}
                style={{ padding: '7px 18px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', color: C.muted, cursor: 'pointer', fontSize: 13 }}>
                {saving ? 'Sauvegarde…' : '💾 Sauvegarder'}
              </button>
              {step < 7 ? (
                <button type="button" onClick={() => setStep(s => s + 1)}
                  style={{ padding: '7px 20px', borderRadius: 8, border: 'none', background: C.accent, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  Suivant →
                </button>
              ) : (
                <button type="button" onClick={handleSave} disabled={saving}
                  style={{ padding: '7px 20px', borderRadius: 8, border: 'none', background: C.green, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  ✓ Finaliser
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Audit Panel ──────────────────────────────────────────────────────────────

function parseValeur(s) {
  if (!s) return '';
  const match = s.replace(/\s/g, '').match(/(\d+)/);
  return match ? parseInt(match[1]) : '';
}

function AuditPanel({ audit, onEdit, onDelete, onClose }) {
  const fetchWithAuth = useAuthFetch();
  const score = computeScore(audit);
  const level = score !== null ? getLevel(score) : null;
  const reco = level ? RECO[level.n] : null;
  const clientName = audit.client_nom || audit.client_nom_saisi || '—';
  const [showOppModal, setShowOppModal] = useState(false);
  const [oppSaving, setOppSaving] = useState(false);
  const [oppForm, setOppForm] = useState({});

  const PRow = ({ label, value }) => value ? (
    <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13 }}>
      <span style={{ color: C.faint, minWidth: 130, flexShrink: 0 }}>{label}</span>
      <span style={{ color: C.text }}>{value}</span>
    </div>
  ) : null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 99 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, width: 500, height: '100vh', background: C.card, borderLeft: `1px solid ${C.border}`, zIndex: 100, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 20px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{clientName}</div>
              <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>
                {audit.date_audit ? fmtDate(audit.date_audit) : '—'}
                {audit.secteur ? ` · ${audit.secteur}` : ''}
                {audit.interlocuteur ? ` · ${audit.interlocuteur}` : ''}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.faint, cursor: 'pointer', fontSize: 20 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {audit.statut_prospect && <Badge text={audit.statut_prospect} color={STATUT_COLORS[audit.statut_prospect] || C.faint} />}
            {audit.livrable_recommande && <Badge text={audit.livrable_recommande} color={C.accent} />}
            {audit.estimation_indicative && <Badge text={audit.estimation_indicative} color={C.green} />}
          </div>
        </div>

        <div className="modal-scroll" style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

          {/* Score */}
          {score !== null && level && (
            <div style={{ background: C.bg, border: `1px solid ${level.color}44`, borderLeft: `4px solid ${level.color}`, borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: level.color }}>{level.label} — {level.desc}</div>
                  <div style={{ fontSize: 11, color: C.faint }}>Score maturité data</div>
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: level.color, fontFamily: "'DM Mono', monospace" }}>{score}%</div>
              </div>
              <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ height: '100%', width: `${score}%`, background: level.color, borderRadius: 3 }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))', gap: 6 }}>
                {[['Qualité','qualite_donnees'],['Accès','accessibilite_donnees'],['Structure','structuration_donnees'],['Maturité','maturite_data_global'],['Digital','maturite_digitale']].map(([lbl, key]) => (
                  <div key={key} style={{ textAlign: 'center', background: C.card, borderRadius: 6, padding: '6px 4px' }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: audit[key] > 0 ? level.color : C.border, fontFamily: "'DM Mono', monospace" }}>{audit[key] || '–'}</div>
                    <div style={{ fontSize: 10, color: C.faint }}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommandation */}
          {reco && (
            <div style={{ background: C.bg, border: `1px solid ${C.accent}33`, borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 6 }}>Recommandation LA DONNÉE</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{reco.service}</span>
                <span style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>{reco.fourchette}</span>
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, lineHeight: 1.5 }}>{reco.diagnostic}</div>
              {reco.maintenance && <div style={{ fontSize: 11, color: C.faint }}>+ {reco.maintenance}</div>}
            </div>
          )}

          {/* Sections recap */}
          {[
            { title: 'Présentation', content: <><PRow label="Phase" value={audit.phase} /><PRow label="CA" value={audit.ca_annuel} /><PRow label="Salariés" value={audit.nb_salaries} />{audit.defis_actuels && <div style={{ fontSize: 12, color: C.muted, background: C.bg, borderRadius: 6, padding: 10, marginTop: 4 }}>{audit.defis_actuels}</div>}</> },
            { title: 'Données & Sources', content: <>
              {[...(audit.sources_fichiers||[]),...(audit.sources_logiciels||[]),...(audit.sources_numeriques||[]),...(audit.sources_physiques||[])].map(s => <span key={s} style={{ display: 'inline-block', marginRight: 4, marginBottom: 4, fontSize: 11, padding: '2px 8px', borderRadius: 10, background: C.accent + '22', color: C.accent }}>{s}</span>)}
              {(audit.sources_table||[]).length > 0 && <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>{audit.sources_table.length} source(s) cartographiée(s)</div>}
              <PRow label="Problèmes" value={audit.problemes_donnees} />
            </> },
            { title: 'Besoins & Objectifs', content: <><PRow label="Problème #1" value={audit.probleme_principal} /><PRow label="Déclencheur" value={audit.declencheur} />{(audit.kpis_table||[]).length > 0 && <div style={{ fontSize: 12, color: C.faint }}>{audit.kpis_table.length} KPI(s) défini(s)</div>}<PRow label="Succès dans 6 mois" value={audit.definition_succes} /></> },
            { title: 'Budget', content: <><PRow label="Budget projet" value={audit.budget_projet} /><PRow label="Maintenance" value={audit.budget_maintenance} /><PRow label="Livraison" value={audit.date_livraison ? fmtDate(audit.date_livraison) : null} /></> },
            { title: 'Synthèse interne', content: <><PRow label="Reformulation" value={audit.reformulation_probleme} /><PRow label="Estimation" value={audit.estimation_indicative} /><PRow label="Relance" value={audit.date_relance ? fmtDate(audit.date_relance) : null} />{audit.actions && <div style={{ fontSize: 12, color: C.muted, background: C.bg, borderRadius: 6, padding: 10, marginTop: 4 }}>{audit.actions}</div>}</> },
          ].map(({ title, content }) => (
            <PanelSection key={title} title={title}>{content}</PanelSection>
          ))}
        </div>

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 10 }}>
          <button onClick={onEdit} style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: C.accent, color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>✏️ Modifier</button>
          <button onClick={() => {
            setOppForm({
              titre: audit.livrable_recommande || (reco ? reco.service : '') || '',
              valeur: parseValeur(audit.estimation_indicative || (reco ? reco.fourchette : '')),
              etape: 'Qualification',
              client_id: audit.client_id || '',
            });
            setShowOppModal(true);
          }} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.green}44`, background: 'none', color: C.green, cursor: 'pointer', fontSize: 13 }}>💼 Opportunité</button>
          <button onClick={onDelete} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.red}44`, background: 'none', color: C.red, cursor: 'pointer', fontSize: 13 }}>🗑</button>
        </div>
      </div>

      {showOppModal && (
        <>
          <div onClick={() => setShowOppModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, zIndex: 201 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, fontSize: 15, color: C.text }}>Créer une opportunité</h3>
              <button onClick={() => setShowOppModal(false)} style={{ background: 'none', border: 'none', color: C.faint, cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: C.faint, marginBottom: 14 }}>Pré-rempli depuis l'audit de <strong style={{ color: C.muted }}>{clientName}</strong></div>
            {[['titre', 'Titre *'], ['valeur', 'Valeur estimée (€)']].map(([k, l]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, color: C.faint, marginBottom: 4, textTransform: 'uppercase' }}>{l}</label>
                <input type={k === 'valeur' ? 'number' : 'text'} value={oppForm[k] || ''} onChange={e => setOppForm(f => ({ ...f, [k]: e.target.value }))}
                  style={{ ...inputStyle }} />
              </div>
            ))}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 11, color: C.faint, marginBottom: 4, textTransform: 'uppercase' }}>Étape</label>
              <select value={oppForm.etape || 'Qualification'} onChange={e => setOppForm(f => ({ ...f, etape: e.target.value }))} style={{ ...selectStyle }}>
                {['Qualification', 'Proposition', 'Négociation', 'Gagnée', 'Perdue'].map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button disabled={oppSaving} onClick={async () => {
                if (!oppForm.titre?.trim()) return;
                setOppSaving(true);
                try {
                  await fetchWithAuth('/api/opportunites', { method: 'POST', body: JSON.stringify({ ...oppForm, valeur: Number(oppForm.valeur) || 0, source: 'Audit data' }) });
                  setShowOppModal(false);
                } finally { setOppSaving(false); }
              }} style={{ flex: 1, padding: '9px', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
                {oppSaving ? 'Création…' : '💼 Créer l\'opportunité'}
              </button>
              <button onClick={() => setShowOppModal(false)} style={{ padding: '9px 16px', borderRadius: 8, background: 'none', border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Annuler</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function PanelSection({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 10, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{ width: '100%', padding: '9px 14px', background: C.bg, border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 12, fontWeight: 700, color: C.muted, display: 'flex', justifyContent: 'space-between' }}>
        {title} <span style={{ color: C.faint }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: '10px 14px' }}>{children}</div>}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AuditPage() {
  const fetchWithAuth = useAuthFetch();
  const [audits, setAudits] = useState([]);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editAudit, setEditAudit] = useState(null);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT, sort: 'date_audit', dir: 'desc' });
      if (search) params.set('search', search);
      const data = await fetchWithAuth(`/api/audits?${params}`).then(r => r.json());
      setAudits(data.data || []);
      setTotal(data.total || 0);
    } catch {}
    setLoading(false);
  }, [fetchWithAuth, page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetchWithAuth('/api/clients?limit=200').then(r => r.json()).then(d => setClients(d.data || [])).catch(() => {});
  }, [fetchWithAuth]);

  const handleSave = async (form) => {
    try {
      if (editAudit?.id) {
        await fetchWithAuth(`/api/audits/${editAudit.id}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await fetchWithAuth('/api/audits', { method: 'POST', body: JSON.stringify(form) });
      }
      setWizardOpen(false); setEditAudit(null); load();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet audit ?')) return;
    await fetchWithAuth(`/api/audits/${id}`, { method: 'DELETE' });
    setSelected(null); load();
  };

  const openEdit = (audit) => { setEditAudit(audit); setWizardOpen(true); setSelected(null); };

  const totalPages = Math.ceil(total / LIMIT);
  const chauds = audits.filter(a => a.statut_prospect === 'Chaud' || a.statut_prospect === 'Confirmé').length;
  const scores = audits.map(a => computeScore(a)).filter(s => s !== null);
  const avgScore = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;

  return (
    <div style={{ padding: 28, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Audits data clients</h1>
        <button onClick={() => { setEditAudit(null); setWizardOpen(true); }}
          style={{ padding: '9px 18px', borderRadius: 8, background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
          + Nouvel audit
        </button>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <KPICard title="Total audits" value={total} color={C.accent} icon="🔬" />
        <KPICard title="Chauds / Confirmés" value={chauds} color={C.red} icon="🔥" />
        <KPICard title="Score moyen" value={avgScore !== null ? `${avgScore}%` : '—'} color={C.green} icon="📊" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="🔍 Rechercher par client ou secteur…"
          style={{ ...inputStyle, maxWidth: 340 }} />
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Client','Secteur','Date','Interlocuteur','Score maturité','Statut','Livrable recommandé'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: C.faint, fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: C.faint }}>Chargement…</td></tr>
              ) : audits.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 50, textAlign: 'center' }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🔬</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.muted, marginBottom: 6 }}>Aucun audit pour l'instant</div>
                  <div style={{ fontSize: 13, color: C.faint, marginBottom: 16 }}>Créez votre premier audit de maturité data</div>
                  <button onClick={() => { setEditAudit(null); setWizardOpen(true); }}
                    style={{ padding: '9px 20px', borderRadius: 8, background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    + Créer un audit
                  </button>
                </td></tr>
              ) : audits.map((a, i) => {
                const score = computeScore(a);
                const level = score !== null ? getLevel(score) : null;
                const clientName = a.client_nom || a.client_nom_saisi || '—';
                return (
                  <tr key={a.id} onClick={() => setSelected(a)}
                    style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: selected?.id === a.id ? C.accent + '15' : i % 2 === 0 ? 'transparent' : '#ffffff05', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = C.accent + '15'}
                    onMouseLeave={e => e.currentTarget.style.background = selected?.id === a.id ? C.accent + '15' : i % 2 === 0 ? 'transparent' : '#ffffff05'}>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: C.text, fontWeight: 500 }}>{clientName}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: C.muted }}>{a.secteur || '—'}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: C.muted }}>{a.date_audit ? fmtDate(a.date_audit) : '—'}</td>
                    <td style={{ padding: '11px 14px', fontSize: 13, color: C.muted }}>{a.interlocuteur || '—'}</td>
                    <td style={{ padding: '11px 14px' }}>
                      {score !== null && level ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                            <div style={{ height: 5, width: 70, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${score}%`, background: level.color, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: level.color, fontFamily: "'DM Mono', monospace" }}>{score}%</span>
                          </div>
                          <span style={{ fontSize: 10, color: level.color }}>{level.label}</span>
                        </div>
                      ) : <span style={{ color: C.border, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      {a.statut_prospect
                        ? <Badge text={a.statut_prospect} color={STATUT_COLORS[a.statut_prospect] || C.faint} />
                        : <span style={{ color: C.border, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: 12, color: C.faint }}>{a.livrable_recommande || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 13, color: C.faint }}>{total} audit{total !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '5px 12px', borderRadius: 6, background: 'none', border: `1px solid ${C.border}`, color: page === 1 ? C.border : C.muted, cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>←</button>
            <span style={{ padding: '5px 12px', fontSize: 13, color: C.muted }}>Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              style={{ padding: '5px 12px', borderRadius: 6, background: 'none', border: `1px solid ${C.border}`, color: page >= totalPages ? C.border : C.muted, cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 13 }}>→</button>
          </div>
        </div>
      </div>

      {selected && !wizardOpen && (
        <AuditPanel audit={selected} onEdit={() => openEdit(selected)} onDelete={() => handleDelete(selected.id)} onClose={() => setSelected(null)} />
      )}
      {wizardOpen && (
        <AuditWizard clients={clients} initial={editAudit} onSave={handleSave} onClose={() => { setWizardOpen(false); setEditAudit(null); }} />
      )}
    </div>
  );
}
